import { isPNDataType } from '@pn/core/domain/data';
import type { User } from '@pn/core/domain/user';
import { moduleItemIds } from '@pn/core/storage/workspace/pnWorkspaceItems';
import { isNil } from 'lodash-es';
import type { WorkspaceItem } from './workspaceItem';

/**
 * How much of an item the API is willing to store:
 * - `full` — the item belongs to the current user and is saved as a whole.
 * - `styleOverride` — the item is shared with (not owned by) the current user,
 *   so only their personal restyling of it can be saved.
 * - `none` — nothing is stored server-side (guest, unsaved item, core PN layer,
 *   module layer or StackDX item).
 */
export type WorkspaceItemPersistence = 'full' | 'styleOverride' | 'none';

export function getWorkspaceItemPersistence(
  item: WorkspaceItem,
  currentUserId: User['id'] | undefined
): WorkspaceItemPersistence {
  if (isNil(currentUserId)) return 'none'; // guest users cannot persist anything
  if (item.isTemporary) return 'none';

  if (item.createdBy?.id === currentUserId) return 'full';

  /* Everything below applies to items shared with the current user. */

  if (item.itemType !== 'list' && item.itemType !== 'layer') return 'none';
  if (isPNDataType(item.id)) return 'none'; // styled via user settings instead
  if (moduleItemIds.includes(item.id)) return 'none'; // module layers have no styles
  if (item.origin === 'stackdx') return 'none'; // StackDX items are not restylable

  return 'styleOverride';
}
